import { AlertTriangle, Info } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/**
 * `info` (gris) : une contrainte à connaître, respectée — rien à faire. `warning` (orange) : à
 * vérifier, ça passe quand même. `critical` (rouge) : à corriger avant d'agir.
 */
export type WarningLevel = 'info' | 'warning' | 'critical';

export type WarningEntry = { level: WarningLevel; message: string };

const LEVEL_ORDER: Record<WarningLevel, number> = { critical: 0, warning: 1, info: 2 };

const LEVEL_STYLES: Record<WarningLevel, { icon: string; bullet: string; noun: string; Icon: typeof Info }> = {
    info: { icon: 'text-muted-foreground', bullet: 'bg-slate-400', noun: 'information', Icon: Info },
    warning: {
        icon: 'text-amber-600 dark:text-amber-400',
        bullet: 'bg-amber-500',
        noun: 'problème',
        Icon: AlertTriangle,
    },
    critical: { icon: 'text-destructive', bullet: 'bg-red-500', noun: 'problème', Icon: AlertTriangle },
};

/**
 * Un seul repère — à la couleur du problème le plus grave — et la liste complète dans l'infobulle.
 *
 * Une même ligne cumule souvent plusieurs alertes : les empiler dans la cellule décale son contenu et
 * noie le peu qui compte. Les pastilles de l'infobulle sont en couleurs fixes, elle s'affiche sur un
 * fond inversé où les jetons de thème passent mal.
 */
export const WarningIndicator = ({
    warnings,
    dataTest,
    className,
}: {
    warnings: WarningEntry[];
    dataTest?: string;
    className?: string;
}) => {
    if (!warnings.length) {
        return null;
    }

    // Le plus grave d'abord : c'est lui qui donne le repère et qui doit se lire en premier.
    const sortedWarnings = [...warnings].sort((first, second) => LEVEL_ORDER[first.level] - LEVEL_ORDER[second.level]);
    const worstLevel = sortedWarnings[0].level;
    const { icon, noun, Icon } = LEVEL_STYLES[worstLevel];

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <span className={cn('shrink-0', icon, className)} data-test={dataTest} data-level={worstLevel}>
                    <Icon className="size-4" aria-label={`${warnings.length} ${noun}(s)`} />
                </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-80 items-start">
                <ul className="flex flex-col gap-1">
                    {sortedWarnings.map(({ level, message }) => (
                        <li key={message} className="flex items-start gap-1.5">
                            <span className={cn('mt-1 size-1.5 shrink-0 rounded-full', LEVEL_STYLES[level].bullet)} />
                            <span>{message}</span>
                        </li>
                    ))}
                </ul>
            </TooltipContent>
        </Tooltip>
    );
};
